import React, { useCallback } from 'react';
import { MODAL_TYPES } from '@components/modals';
import { CleanupWizardEntry } from '@features/practice-cleanup';
import { useCan, useOrganizationId } from '@features/user';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useModal } from '@store/hooks';

import { useOrganizationAppointmentTypes } from '../store/hooks';

import { TypeCatalogEntry } from './TypeCatalogEntry';

export const PracticeAppointmentTypesPage: React.FC = () => {
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const organizationId = useOrganizationId();
  const { open: openModal } = useModal();
  const { appointmentTypes, isLoading, error } = useOrganizationAppointmentTypes(
    organizationId,
    undefined,
    { skip: !canManageAppointmentTypes },
  );

  const openAppointmentTypes = useCallback(() => {
    openModal(MODAL_TYPES.APPOINTMENT_TYPES_MANAGEMENT, {
      source: 'practice_appointment_types_page',
      maxWidth: 'md',
      fullScreenOnMobile: true,
    });
  }, [openModal]);

  if (!canManageAppointmentTypes) {
    return (
      <Alert severity="info">
        You do not have permission to manage practice appointment types.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Appointment Types
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 680 }}>
              Control the visit options your team chooses from while scheduling and preparing
              encounters. These defaults help each appointment start with the right duration, color,
              note template, forms, and reminders.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAppointmentTypes}
            sx={{ justifySelf: { xs: 'stretch', md: 'end' } }}
          >
            Manage Appointment Types
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EventAvailableIcon color="primary" />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Current practice setup
              </Typography>
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading appointment types...
                  </Typography>
                </Box>
              ) : error ? (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  Appointment types could not be loaded.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {appointmentTypes.length === 1
                    ? '1 appointment type is available to the practice.'
                    : `${appointmentTypes.length} appointment types are available to the practice.`}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            p: { xs: 2, md: 3 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <SetupSignal
            icon={<ScheduleIcon />}
            title="Scheduling"
            body="Name, color, duration, and visit mode."
          />
          <SetupSignal
            icon={<ArticleIcon />}
            title="Charting"
            body="The note template that should open for each visit type."
          />
          <SetupSignal
            icon={<AssignmentTurnedInIcon />}
            title="Follow-through"
            body="Intake forms and appointment reminders."
          />
        </Box>
      </Paper>

      {/* Housekeeping tools (GDN-2335): the evergreen catalog and the
          one-shot cleanup wizard — sibling entries, independently gated. */}
      <TypeCatalogEntry />
      <CleanupWizardEntry />
    </Stack>
  );
};

const SetupSignal: React.FC<{
  icon: React.ReactElement;
  title: string;
  body: string;
}> = ({ icon, title, body }) => (
  <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0 }}>
    <Box sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {body}
      </Typography>
    </Box>
  </Box>
);

export default PracticeAppointmentTypesPage;
